/* eslint-disable import/no-extraneous-dependencies */
import 'crux/dist/index.css';
import { DatePicker as DateSelector, DateInput } from 'crux';
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withTheme } from 'styled-components';
import get from 'lodash/get';
import { at } from 'lodash';
import { updateAnswersArray } from '../redux/actions';
import { updateQuestionAnswerTimeStamp } from '../redux/actions/questions';
import {
  getLabelProps,
  getValidators,
  getWidth,
  getTooltipProps,
  getCSSVariables,
  getTimeStampData,
} from './utils';
import { scrollQuestionIntoView, getUTCTimestamp, isQuotePage } from '../utils';
import { InputWrapper } from './utils/styles';

const initialState = {
  // adding to make sure api call is done only when date is edited
  isEdited: false,
};

const dayClassName = () => 'app--datepicker--day';
const monthClassName = () => 'app--datepicker--month';
const yearClassName = () => 'app--datepicker--year';

const DatePicker = ({
  question,
  optionalText,
  theme,
  handleChange,
  handleDateBlur,
  isEditMode,
  screenViewport,
  lastFocusedElementId,
  role,
  updateTimeStamp,
  meta,
  // updateAnswerArrayInSocket,
}) => {
  const [state, setState] = useState(initialState);
  const showOptionalText = get(theme, 'config.theme.global.showOptionalText', false);
  const {
    pageId,
    config: {
      theme: {
        pageOverrides,
        components,
        global: { colorScheme, errorMessageColor },
      },
    },
  } = theme;
  const labelProps = getLabelProps('input', components, pageId, pageOverrides);
  const {
    question_id: questionId,
    question_text: questionText,
    hint_title: tooltipTitle,
    hint_text: tooltipBody,
    hint_html: tooltipHTML,
    response,
    tooltip,
    question_status: questionStatus,
    helper_text: helperText,
    transaction_log: transactionLog,
    last_submitted_at: lastSubmittedAt,
    show_as_required: showAsRequired,
  } = question;
  const reflexiveQFlag = get(question, 'reflexive_question_flag', false);
  const childQs = get(question, 'child_questions', false);

  const blurActionRef = useRef(false);
  const [isBlur, setIsBlur] = useState(false);
  const timeStampRef = useRef(null);

  const handleUpdateTimeStamp = () => {
    updateTimeStamp(getTimeStampData(timeStampRef, transactionLog, meta));
  };
  const tooltipProps = getTooltipProps(tooltipTitle, tooltipBody, tooltipHTML || tooltip);
  const inputRef = useRef();

  const validators = getValidators(question);
  const size = get(question, 'properties.size.value', '');
  if (validators.autoFormat && !validators.autoFormat.value) {
    validators.autoFormat.value = 'MM/DD/YYYY';
  }

  // Tab Navigation
  useEffect(() => {
    const shouldFocus = lastFocusedElementId === questionId;
    if (
      inputRef &&
      inputRef.current &&
      shouldFocus &&
      typeof inputRef.current.focus === 'function'
    ) {
      inputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    if (blurActionRef.current || isBlur) {
      handleUpdateTimeStamp();
      // updateAnswerArrayInSocket(reflexiveQFlag);
    }
  }, [isBlur, blurActionRef.current]);

  useEffect(() => {
    if (isEditMode || question.response) {
      handleChange(question.response, false, false, true);
      updateTimeStamp({ transactionLog, lastSubmittedAt }, true);
    }
  }, []);

  // Determines props for Calendar Popper (visible/hidden)
  const { showWCAGCloseBtn } = components.datepicker;
  const hasCalendarPopper = components.datepicker.showPopper;
  const showPopperProps = hasCalendarPopper
    ? {}
    : { showCalendarPopper: false, showCalendarIcon: false };

  // Checks if guide is enabled or not
  const hasGuide = get(components, 'datepicker.guide', false);
  const isDateChange = get(components, 'datepicker.isDateChange', false);
  // const trackCalendarPopper = get(components, 'datepicker.trackCalendarPopper', false);

  // overrides floatingLabel to false in case of guide feature enabled
  const cssVariables = getCSSVariables(theme);
  const _font = at(cssVariables, ['pageOverride.font', 'global.font']);
  const floatingLabel = hasGuide ? false : labelProps.floatingLabel;
  const themeConfig = get(labelProps, 'themeConfiguration', {});
  // eslint-disable-next-line dot-notation
  const font = _font?.[0]?.['datePicker'] || _font?.[1]?.['datePicker'];

  const commonProps = {
    labelText: questionText,
    id: questionId,
    response,
    color: colorScheme,
    floatingLabel,
    width: getWidth('datepicker', pageId, pageOverrides),
    guide: hasCalendarPopper ? hasGuide : null,
    yearPlaceholder: !hasCalendarPopper ? 'YYYY' : null,
    monthPlaceholder: !hasCalendarPopper ? 'MM' : null,
    dayPlaceholder: !hasCalendarPopper ? 'DD' : null,
    questionStatus,
    ...labelProps,
    ...tooltipProps,
    ...validators,
    isDateChange,
  };

  const hideCalendarIcon = get(question, 'properties.hide_calendar_icon.value');
  if (hideCalendarIcon) {
    showPopperProps.showCalendarIcon = false;
  }
  let datepicker;

  const handleBlur = (value, preventReflexiveCall, error, isDateInput) => {
    if (!isDateInput) {
      handleDateBlur(value, preventReflexiveCall, error);
    }
  };

  const customHandleOnBlur = () => {
    blurActionRef.current = true;
    setIsBlur(true);
  };

  const handleFocus = (e) => {
    const element = e.target;
    blurActionRef.current = false;
    setIsBlur(false);
    scrollQuestionIntoView({
      element,
      theme,
      role,
    });
    timeStampRef.current.timeStart = getUTCTimestamp();
  };

  if (hasCalendarPopper) {
    datepicker = (
      <InputWrapper name='input-wrapper' ref={ timeStampRef } onBlur={ () => customHandleOnBlur() }>
        <DateSelector
          inputRef={ (el) => {
            inputRef.current = el;
          } }
          labelText={ `${questionText} ${optionalText}`.trim() }
          id={ questionId }
          onChange={ (value, errorObj, isPopperClick) => {
            handleChange(value, errorObj, true, errorObj.error);
            if (typeof isPopperClick === 'undefined' && !reflexiveQFlag && !childQs)
              handleUpdateTimeStamp();
          } }
          onValueChangeRaw={ () => {
            setState({
              ...state,
              isEdited: true,
            });
          } }
          guide={ hasGuide }
          response={ response }
          color={ colorScheme }
          onBlur={ (value, error) => {
            if (!isDateChange) {
              handleBlur(value, state.isEdited, error);
            }
          } }
          onFocus={ handleFocus }
          { ...showPopperProps }
          { ...labelProps }
          { ...validators }
          { ...tooltipProps }
          width={ getWidth('datepicker', pageId, pageOverrides, size, screenViewport) }
          floatingLabel={ floatingLabel }
          themeConfiguration={ { ...themeConfig, font } }
          errorMessageColor={ errorMessageColor }
          isDateChange
          trackCalendarPopper
          helperText={ helperText }
          showWCAGCloseBtn={ showWCAGCloseBtn }
          dayClassName={ dayClassName }
          monthClassName={ monthClassName }
          yearClassName={ yearClassName }
          isSetOpen={ false }
          isAutoCleared={ !validators?.required?.value }
          showOptionalText={ showOptionalText && !isQuotePage() }
          isWcagOptional={ !(showAsRequired || question?.validations?.required?.value) }
        />
      </InputWrapper>
    );
  } else {
    datepicker = (
      <div
        onFocus={ handleFocus }
        onBlur={ (e) => {
          handleUpdateTimeStamp();
          handleBlur(e, true, {}, true);
        } }
        ref={ timeStampRef }
      >
        <DateInput
          { ...commonProps }
          onChange={ (value, error) => {
            handleChange(value, error, true);
          } }
          errorMessageColor={ errorMessageColor }
        />
      </div>
    );
  }
  return datepicker;
};

DatePicker.defaultProps = {
  sectionIndex: -1,
};
DatePicker.propTypes = {
  /* eslint-disable react/forbid-prop-types */
  question: PropTypes.object.isRequired,
  /* eslint-disable react/no-unused-prop-types */
  sectionIndex: PropTypes.number,
  handleChange: PropTypes.func.isRequired,
};
const mapStateToProps = (state) => {
  const { meta, questions } = state;
  const { screenViewport } = meta;
  const { lastFocusedElementId } = questions;
  const role = get(state, 'questions.role', '');
  return {
    screenViewport,
    lastFocusedElementId,
    role,
    meta: state.meta,
  };
};
const mapDispatchToProps = (dispatch, props) => {
  // question_status='absent' means the question is optional and for date input
  // if the date is null we don't consider it as error if DOB is an optional question
  // and there is no error in the date entered. This case is seen in the beneficiary
  // section of Nationwide where DOB is optional and date can be null
  const questionStatus = get(props, 'question.question_status', '');
  let error;
  const components = get(props, 'theme.config.theme.components', {});
  const hasCalPopper = components?.datepicker?.showPopper;
  const transactionLog = get(props, 'question.transaction_log', []);
  const questionId = get(props, 'question.question_id', '');

  return {
    handleChange: (response, hasError, hasEdit = false, preventReflexiveCall) => {
      error = hasError.error;
      if (questionStatus === 'absent' && !(response instanceof Date) && hasError.error) {
        error = false;
      }
      const auditRequired = get(props, 'question.audit_required', false);
      dispatch(
        updateAnswersArray({
          questionId,
          response: response || '',
          hasEdit,
          sectionIndex: props.sectionIndex,
          preventReflexiveCall: hasCalPopper ? preventReflexiveCall : true,
          propQuestion: props.question,
          isError: error,
          listParentId: props.listParentId,
          listIndex: props.listIndex,
          auditRequired,
          ignoreChildQuestionsOn: true,
          isNestedQ: props.isNestedQ,
          parentListIndex: props.parentListIndex,
          transactionLog,
          // validate: get(props, 'question.dob_valid_flag', false),
        }),
      );
    },
    handleDateBlur: (response, isEdited, hasError) => {
      const auditRequired = get(props, 'question.audit_required', false);
      dispatch(
        updateAnswersArray({
          questionId,
          response: response || '',
          preventReflexiveCall: !hasCalPopper ? isEdited : true,
          sectionIndex: props.sectionIndex,
          isError: hasError && hasError.error,
          validate: get(props, 'question.dob_valid_flag', false) && isEdited,
          auditRequired,
          isNestedQ: props.isNestedQ,
          parentListIndex: props.parentListIndex,
        }),
      );
    },
    updateTimeStamp: (timeStampData, isPrefilled = false) => {
      dispatch(
        updateQuestionAnswerTimeStamp(questionId, timeStampData, props.sectionIndex, isPrefilled),
      );
    },
    // updateAnswerArrayInSocket: (reflexiveQFlag = false) => {
    //   dispatch(updateQuestionAnswerInSocket(questionId, reflexiveQFlag));
    // },
  };
};

export default withTheme(connect(mapStateToProps, mapDispatchToProps)(DatePicker));
